import React from "react";
import { motion } from "motion/react";
import {
  Award,
  Flame,
  Target,
  Layers,
  Star,
  Zap,
  Crown,
  ShieldCheck,
  Trophy,
  CheckCircle2,
} from "lucide-react";

export const BADGE_ICON_MAP = {
  Flame,
  Target,
  Layers,
  Award,
  Star,
  Zap,
  Crown,
  ShieldCheck,
  Trophy,
};

export const BADGE_STAT_LABELS = {
  points: "career points",
  three_pm: "career threes",
  double_doubles: "double-doubles",
  rebounds: "career rebounds",
  assists: "career assists",
  steals: "career steals",
};

export const groupBadgeDefs = (defs) => {
  const families = {};
  const standalone = [];
  defs.forEach((b) => {
    if (!b.family) {
      standalone.push(b);
      return;
    }
    if (!families[b.family]) families[b.family] = [];
    families[b.family].push(b);
  });
  Object.values(families).forEach((tiers) =>
    tiers.sort((a, b) => a.tier - b.tier),
  );
  return { families, standalone };
};

export const FamilyBadgeCard = ({ tiers, unlockedKeys, currentValue }) => {
  const highestUnlocked = [...tiers]
    .reverse()
    .find((t) => unlockedKeys.has(t.key));
  const nextTier = tiers.find((t) => !unlockedKeys.has(t.key));
  const display = highestUnlocked || tiers[0];
  const Icon = BADGE_ICON_MAP[display.icon] || Award;
  const unlocked = !!highestUnlocked;

  const pct = nextTier
    ? Math.min(100, Math.round((currentValue / nextTier.threshold) * 100))
    : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-3xl p-5 border ${
        unlocked
          ? "border-orange-200 dark:border-orange-900 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20"
          : "border-gray-100 dark:border-gray-800"
      }`}
    >
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-3 ${
          unlocked
            ? "bg-gradient-to-br from-orange-500 to-amber-500"
            : "bg-gray-100 dark:bg-gray-800"
        }`}
      >
        <Icon
          size={24}
          className={
            unlocked ? "text-white" : "text-gray-400 dark:text-gray-600"
          }
        />
      </div>
      <p
        className={`font-semibold ${unlocked ? "dark:text-white" : "text-gray-500 dark:text-gray-500"}`}
      >
        {display.name}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {display.description}
      </p>

      {nextTier ? (
        <>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-400 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            {currentValue} / {nextTier.threshold} games{" "}
            {unlocked ? `· next: ${nextTier.name}` : ""}
          </p>
        </>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 dark:text-orange-400">
          <CheckCircle2 size={14} /> Max tier reached
        </span>
      )}
    </motion.div>
  );
};

export const BadgeCard = ({ badge, unlocked, currentValue }) => {
  const Icon = BADGE_ICON_MAP[badge.icon] || Award;
  const pct = Math.min(100, Math.round((currentValue / badge.threshold) * 100));
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`rounded-3xl p-5 border transition-colors ${
        unlocked
          ? "border-orange-200 dark:border-orange-900 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20"
          : "border-gray-100 dark:border-gray-800"
      }`}
    >
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-2xl mb-3 ${
          unlocked
            ? "bg-gradient-to-br from-orange-500 to-amber-500"
            : "bg-gray-100 dark:bg-gray-800"
        }`}
      >
        <Icon
          size={24}
          className={
            unlocked ? "text-white" : "text-gray-400 dark:text-gray-600"
          }
        />
      </div>
      <p
        className={`font-semibold ${unlocked ? "dark:text-white" : "text-gray-500 dark:text-gray-500"}`}
      >
        {badge.name}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {badge.description}
      </p>
      {unlocked ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 dark:text-orange-400">
          <CheckCircle2 size={14} /> Unlocked
        </span>
      ) : (
        <>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-400 rounded-full transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            {currentValue} / {badge.threshold}{" "}
            {BADGE_STAT_LABELS[badge.stat_key]}
          </p>
        </>
      )}
    </motion.div>
  );
};
